/**
 * The settings draft (web.md §4 "unsaved-changes guard", `useDraft`): an editor keeps its form value
 * here; editing marks it dirty; the shell's Save button, Ctrl/Cmd + S, the Keep editing / Discard / Save
 * dialog and `beforeunload` all act on the one registered draft.
 *
 *   const draft = useDraft({ initial: topic, isNew, onSave: async (v) => (await engine.topics.update(id, v)).id });
 *   <Field value={draft.value.name} onChange={(name) => draft.set((d) => ({ ...d, name }))} />
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportError } from '../../app/errors.js';
import { showToast } from '../../app/Toast.js';
import { ui } from '../../app/ui-store.js';
import { useSettingsLocation } from './context.js';

export interface DraftOptions<T> {
  /** The saved value. While the draft is clean, a changed `initial` (after a save, or a change from
   *  another device) replaces the form value. */
  initial: T;
  /** A record that does not exist yet (starts dirty, like the prototype's "New item"). */
  isNew?: boolean;
  /** Persist the value. Throw (a DigesterError) to refuse; return the record id to open it after creating. */
  onSave(value: T): Promise<string | void>;
  /** Toast after a successful save. Default "Changes saved". */
  savedMessage?: string;
}

export interface Draft<T> {
  value: T;
  set(update: T | ((previous: T) => T)): void;
  dirty: boolean;
  isNew: boolean;
  /** Validate the form (#settings-form) and save. Resolves false when invalid or refused. */
  save(): Promise<boolean>;
  /** Back to the saved value. */
  discard(): void;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

export function useDraft<T>(options: DraftOptions<T>): Draft<T> {
  const { initial, isNew = false } = options;
  const navigate = useNavigate();
  const location = useSettingsLocation();
  const [value, setValue] = useState<T>(initial);
  const [dirty, setDirty] = useState(isNew);
  const valueRef = useRef(value);
  valueRef.current = value;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const initialRef = useRef(initial);
  const onSaveRef = useRef(options.onSave);
  onSaveRef.current = options.onSave;
  const savedMessage = options.savedMessage ?? 'Changes saved';

  // Follow the saved record while there is nothing to lose.
  useEffect(() => {
    if (same(initial, initialRef.current)) return;
    initialRef.current = initial;
    if (!dirtyRef.current) setValue(initial);
  }, [initial]);

  const set = useCallback((update: T | ((previous: T) => T)) => {
    setValue((prev) => (typeof update === 'function' ? (update as (p: T) => T)(prev) : update));
    dirtyRef.current = true;
    setDirty(true);
  }, []);

  const discard = useCallback(() => {
    setValue(initialRef.current);
    dirtyRef.current = false;
    setDirty(false);
    // Clean in the store at once, so a navigation right after a discard is not blocked by the guard.
    const current = ui.get().draft;
    if (current?.dirty) ui.set({ draft: { ...current, dirty: false } });
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const form = document.getElementById('settings-form');
    if (form instanceof HTMLFormElement && !form.reportValidity()) return false;
    const initialAtStart = initialRef.current;
    try {
      const id = await onSaveRef.current(valueRef.current);
      dirtyRef.current = false;
      setDirty(false);
      // A save that writes several records refreshes `initial` while the form is still dirty: show what
      // the engine stored (normalised, re-sorted) rather than the typed value. Later changes of
      // `initial` are followed by the effect above.
      if (!same(initialRef.current, initialAtStart)) setValue(initialRef.current);
      // Mark clean in the store at once, so the navigation below is not blocked by the guard.
      const current = ui.get().draft;
      if (current) ui.set({ draft: { ...current, dirty: false } });
      showToast(savedMessage);
      if (isNew && typeof id === 'string' && location) navigate(`/settings/${location.category}/${encodeURIComponent(id)}`, { replace: true });
      return true;
    } catch (error) {
      reportError(error);
      return false;
    }
  }, [isNew, location, navigate, savedMessage]);

  // Register with the shell (top-bar Save, Ctrl+S, the guard, the status label).
  useEffect(() => {
    const registration = { dirty, isNew, save, discard };
    ui.set({ draft: registration });
    return () => {
      if (ui.get().draft === registration) ui.set({ draft: null });
    };
  }, [dirty, isNew, save, discard]);

  return { value, set, dirty, isNew, save, discard };
}
